import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, Input, Label } from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { ProductContext } from "../../store";
import { PostData } from "../../services/FetchData";
import { MONITORING_TIMEZONE_OPTIONS } from "../AiVisibility/Monitoring/monitoringAdapters";
import { widgetSelectStyles as defaultSelectStyles } from "./widgetSettingsStyles";

const WEEK_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DOW_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function defaultWeekSchedule() {
	return WEEK_DISPLAY_ORDER.map((dow) => ({
		dow,
		enabled: dow >= 1 && dow <= 5,
		start: "00:00",
		end: "23:59",
	}));
}

function splitHoursFromApi(parsed) {
	const byDow = {};
	const extras = [];
	for (const slot of parsed || []) {
		const dow = Number(slot.dow);
		if (Number.isNaN(dow)) continue;
		const row = {
			dow,
			start: slot.start || "00:00",
			end: slot.end || "23:59",
		};
		if (byDow[dow] === undefined) {
			byDow[dow] = { ...row, enabled: true };
		} else {
			extras.push(row);
		}
	}
	const week = WEEK_DISPLAY_ORDER.map(
		(dow) =>
			byDow[dow] || {
				dow,
				enabled: false,
				start: "00:00",
				end: "23:59",
			}
	);
	return { week, extras };
}

function mergeHoursForApi(week, extras) {
	const out = [];
	for (const row of week) {
		if (row.enabled) {
			out.push({ dow: row.dow, start: row.start, end: row.end });
		}
	}
	for (const row of extras) {
		out.push({ dow: row.dow, start: row.start, end: row.end });
	}
	return out;
}

function ensureTimezoneOptions(current) {
	const values = MONITORING_TIMEZONE_OPTIONS.map((o) => o.value);
	if (current && !values.includes(current)) {
		return [{ value: current, label: current }, ...MONITORING_TIMEZONE_OPTIONS];
	}
	return MONITORING_TIMEZONE_OPTIONS;
}

const LiveChatSettingsPanel = ({ selectStyles = defaultSelectStyles }) => {
	const { currentWebsiteId } = useContext(ProductContext);
	const websiteId = currentWebsiteId || Cookies.get("_website_id");

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [liveChatEnabled, setLiveChatEnabled] = useState(false);
	const [visitorHistory, setVisitorHistory] = useState(false);
	const [timezone, setTimezone] = useState("America/Chicago");
	const [offlineMessage, setOfflineMessage] = useState("");
	const [week, setWeek] = useState(defaultWeekSchedule);
	const [extraHours, setExtraHours] = useState([]);

	const timezoneOptions = useMemo(() => ensureTimezoneOptions(timezone), [timezone]);
	const timezoneSelectOptions = useMemo(
		() => timezoneOptions.map((opt) => ({ value: opt.value, label: opt.value })),
		[timezoneOptions]
	);
	const selectedTimezone = useMemo(
		() => timezoneSelectOptions.find((o) => o.value === timezone) || timezoneSelectOptions[0],
		[timezone, timezoneSelectOptions]
	);

	const load = useCallback(async () => {
		if (!websiteId) {
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const res = await PostData("ms1", "api/v1/aiassistant/liveChatSettings", { websiteId });
			if (res?.status === "success" && res?.data?.settings) {
				const s = res.data.settings;
				setLiveChatEnabled(String(s.liveChat).toLowerCase() === "enable");
				setVisitorHistory(String(s.liveChatVisitorHistory).toLowerCase() === "enable");
				setTimezone(s.liveChatTimezone || "America/Chicago");
				setOfflineMessage(s.liveChatOfflineMessage || "");
				let parsed = [];
				try {
					parsed = JSON.parse(s.liveChatBusinessHours || "[]");
				} catch {
					parsed = [];
				}
				if (Array.isArray(parsed) && parsed.length) {
					const split = splitHoursFromApi(parsed);
					setWeek(split.week);
					setExtraHours(split.extras);
				} else {
					setWeek(defaultWeekSchedule());
					setExtraHours([]);
				}
			}
		} catch (e) {
			console.error(e);
			toast.error("Could not load live chat settings");
		} finally {
			setLoading(false);
		}
	}, [websiteId]);

	useEffect(() => {
		load();
	}, [load]);

	const save = async () => {
		if (!websiteId) return;
		setSaving(true);
		try {
			const res = await PostData("ms1", "api/v1/aiassistant/saveLiveChatSettings", {
				websiteId,
				settings: {
					liveChat: liveChatEnabled ? "enable" : "disable",
					liveChatVisitorHistory: visitorHistory ? "enable" : "disable",
					liveChatTimezone: timezone,
					liveChatOfflineMessage: offlineMessage,
					liveChatBusinessHours: JSON.stringify(mergeHoursForApi(week, extraHours)),
				},
			});
			if (res?.status === "success") {
				toast.success("Live chat settings saved");
			} else {
				toast.error(res?.message || "Save failed");
			}
		} catch (e) {
			console.error(e);
			toast.error("Save failed");
		} finally {
			setSaving(false);
		}
	};

	const updateWeekRow = (idx, patch) => {
		setWeek((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
	};

	const copyMondayToAllDays = () => {
		const mon = week.find((r) => r.dow === 1) || week[0];
		setWeek((prev) =>
			prev.map((row) => ({
				...row,
				start: mon.start,
				end: mon.end,
			}))
		);
	};

	const updateExtra = (idx, field, value) => {
		setExtraHours((prev) =>
			prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row))
		);
	};

	const removeExtra = (idx) => {
		setExtraHours((prev) => prev.filter((_, i) => i !== idx));
	};

	if (!websiteId) {
		return null;
	}

	return (
		<Card className="settings-card live-chat-settings-panel">
			<CardBody className="settings-card-body">
				<h6>Live Chat Settings</h6>
				{loading ? (
					<p className="text-muted small mb-0">Loading…</p>
				) : (
					<>
						<div className="live-chat-settings-panel__toggles">
							<div className="form-check form-switch">
								<input
									className="form-check-input"
									type="checkbox"
									id="liveChatEnable"
									checked={liveChatEnabled}
									onChange={(e) => setLiveChatEnabled(e.target.checked)}
								/>
								<Label className="form-check-label" htmlFor="liveChatEnable">
									Enable live chat handoff on storefront
								</Label>
							</div>
							<div className="form-check form-switch">
								<input
									className="form-check-input"
									type="checkbox"
									id="liveChatVisitorHistory"
									checked={visitorHistory}
									onChange={(e) => setVisitorHistory(e.target.checked)}
								/>
								<Label className="form-check-label" htmlFor="liveChatVisitorHistory">
									Show live chat transcript to visitors on the storefront
								</Label>
							</div>
						</div>

						<div className="live-chat-settings-panel__fields-row">
							<div className="live-chat-settings-panel__field">
								<Label htmlFor="liveChatTz" className="form-label">
									Timezone
								</Label>
								<Select
									inputId="liveChatTz"
									value={selectedTimezone}
									onChange={(opt) => setTimezone(opt?.value || "America/Chicago")}
									options={timezoneSelectOptions}
									styles={selectStyles}
									isSearchable
									menuPlacement="auto"
								/>
							</div>
							<div className="live-chat-settings-panel__field live-chat-settings-panel__field--grow">
								<Label htmlFor="liveChatOfflineMsg" className="form-label">
									Offline message
								</Label>
								<Input
									id="liveChatOfflineMsg"
									value={offlineMessage}
									onChange={(e) => setOfflineMessage(e.target.value)}
									placeholder="Our team is away. Leave a message and we will email you back."
								/>
							</div>
						</div>

						<div className="live-chat-settings-hours">
							<div className="live-chat-settings-hours__header">
								<span className="live-chat-settings-hours__label">Business hours</span>
								<Button
									color="link"
									className="live-chat-settings-hours__copy"
									type="button"
									onClick={copyMondayToAllDays}
								>
									Copy Monday to all days
								</Button>
							</div>
							<table className="live-chat-settings-hours-table">
								<thead>
									<tr>
										<th className="live-chat-settings-hours-table__check" aria-hidden="true" />
										<th>Day</th>
										<th>Opens</th>
										<th>Closes</th>
									</tr>
								</thead>
								<tbody>
									{week.map((row, idx) => (
										<tr key={row.dow} className={row.enabled ? "" : "is-disabled"}>
											<td className="live-chat-settings-hours-table__check">
												<input
													type="checkbox"
													className="form-check-input live-chat-settings__day-check"
													checked={row.enabled}
													onChange={(e) =>
														updateWeekRow(idx, { enabled: e.target.checked })
													}
													aria-label={`Enable ${DOW_FULL[row.dow]}`}
												/>
											</td>
											<td className="live-chat-settings-hours-table__day-name">
												{DOW_FULL[row.dow]}
											</td>
											<td>
												<Input
													type="time"
													className="live-chat-settings__time"
													value={row.start}
													disabled={!row.enabled}
													onChange={(e) => updateWeekRow(idx, { start: e.target.value })}
												/>
											</td>
											<td>
												<Input
													type="time"
													className="live-chat-settings__time"
													value={row.end}
													disabled={!row.enabled}
													onChange={(e) => updateWeekRow(idx, { end: e.target.value })}
												/>
											</td>
										</tr>
									))}
								</tbody>
							</table>

							{extraHours.length > 0 ? (
								<table className="live-chat-settings-hours-table live-chat-settings-hours-table--extra mt-2">
									<tbody>
										{extraHours.map((row, idx) => (
											<tr key={`extra-${idx}`}>
												<td className="live-chat-settings-hours-table__check" />
												<td>
													<Input
														type="select"
														bsSize="sm"
														className="live-chat-settings__day"
														value={row.dow}
														onChange={(e) =>
															updateExtra(idx, "dow", parseInt(e.target.value, 10))
														}
													>
														{DOW_SHORT.map((label, dow) => (
															<option key={dow} value={dow}>
																{label}
															</option>
														))}
													</Input>
												</td>
												<td>
													<Input
														type="time"
														className="live-chat-settings__time"
														value={row.start}
														onChange={(e) => updateExtra(idx, "start", e.target.value)}
													/>
												</td>
												<td>
													<Input
														type="time"
														className="live-chat-settings__time"
														value={row.end}
														onChange={(e) => updateExtra(idx, "end", e.target.value)}
													/>
												</td>
												<td>
													<Button
														color="link"
														className="live-chat-settings__remove"
														type="button"
														onClick={() => removeExtra(idx)}
													>
														Remove
													</Button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							) : null}

							<Button
								color="link"
								className="live-chat-settings-panel__add-row"
								type="button"
								onClick={() =>
									setExtraHours((h) => [...h, { dow: 1, start: "09:00", end: "17:00" }])
								}
							>
								+ Add hours row
							</Button>
						</div>

							<div className="live-chat-settings-panel__actions">
							<Button color="primary" className="conversionBox" onClick={save} disabled={saving}>
								{saving ? "Saving…" : "Save live chat settings"}
							</Button>
						</div>
					</>
				)}
			</CardBody>
		</Card>
	);
};

export default LiveChatSettingsPanel;
